/* Altruan Spar-Abo Widget
 * -----------------------------------------------------------------
 * Steuert den nativen Shopify-Parameter `selling_plan` im
 * Produktformular. Kein Bezug zum Seal-Widget-JS: Seal übernimmt die
 * Abos automatisch nach dem Checkout (Selling Plans sind native
 * Shopify-Funktionalität).
 *
 * Das Markup wird vollständig serverseitig in
 * snippets/altruan-abo-widget.liquid gerendert – dieses Script
 * ergänzt nur Interaktivität. Ohne JS bleibt die Seite als
 * Einmalkauf voll funktionsfähig.
 *
 * Übersetzbare Texte kommen aus den Theme-Locale-Dateien und werden
 * vom Liquid-Snippet ins Daten-JSON gerendert ("strings").
 */
(() => {
  'use strict';

  if (customElements.get('altruan-abo-widget')) return;

  class AltruanAboWidget extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('script[data-abo-data]');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (_) {
        return;
      }

      this.fmt = new Intl.NumberFormat(this.data.locale || 'de', {
        style: 'currency',
        currency: this.data.currency || 'EUR',
      });

      this.strings = this.data.strings || {};
      this.mode = this.dataset.defaultMode === 'subscription' ? 'subscription' : 'one_time';

      this.form = this.findProductForm();
      this.planInput = this.ensureSellingPlanInput();

      this.addEventListener('click', (event) => {
        const tile = event.target.closest('[data-abo-mode]');
        if (tile) {
          this.mode = tile.dataset.aboMode === 'subscription' ? 'subscription' : 'one_time';
          this.apply();
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.apply();
      });

      this.watchVariantChanges();
      this.apply();
    }

    disconnectedCallback() {
      if (this.variantObserver) this.variantObserver.disconnect();
    }

    /* Das zugehörige Produktformular: erst innerhalb der eigenen
       Section suchen, dann seitenweit (Fallback).
       Wichtig: Dawn/Ignite rendert zusätzlich ein verstecktes
       Shop-Pay-Installment-Formular mit action="/cart/add" – wir
       brauchen das ECHTE Kauf-Formular (mit Kaufen-Button bzw. im
       <product-form>-Element). */
    findProductForm() {
      const scopes = [this.closest('.shopify-section'), document];
      for (const scope of scopes) {
        if (!scope) continue;
        const forms = [...scope.querySelectorAll('form[action*="/cart/add"]')]
          .filter((f) => f.querySelector('[name="id"]'));
        const buyForm = forms.find(
          (f) => f.querySelector('[name="add"]') || f.closest('product-form')
        );
        if (buyForm) return buyForm;
        if (forms.length) return forms[0];
      }
      return null;
    }

    /* Hidden Input für den Selling Plan anlegen (oder vorhandenen
       wiederverwenden). Bei Einmalkauf wird er `disabled`, damit er
       nicht mitserialisiert wird. */
    ensureSellingPlanInput() {
      if (!this.form) return null;
      let input = this.form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.disabled = true;
        this.form.appendChild(input);
      }
      return input;
    }

    /* Variantenwechsel erkennen: Ignite/Dawn aktualisiert das
       versteckte [name="id"]-Input. Change-Events plus
       MutationObserver decken beide Update-Wege ab. */
    watchVariantChanges() {
      if (!this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput) return;

      this.form.addEventListener('change', () => {
        queueMicrotask(() => this.apply());
      });

      this.variantObserver = new MutationObserver(() => this.apply());
      this.variantObserver.observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }

    currentVariant() {
      const id = this.form && this.form.querySelector('[name="id"]')?.value;
      return this.data.variants[id] || Object.values(this.data.variants)[0];
    }

    selectedPlanId() {
      const checked = this.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    }

    money(cents) {
      return this.fmt.format(cents / 100);
    }

    /* Zentrale Update-Routine: Kachel-Zustände, Selling-Plan-Input
       und alle Preisanzeigen. */
    apply() {
      // Falls das Theme das Formular neu gerendert hat (z. B. beim
      // Variantenwechsel), Formular und Hidden Input neu verdrahten.
      if (!this.form || !this.form.isConnected) {
        this.form = this.findProductForm();
        this.planInput = this.ensureSellingPlanInput();
      }

      const variant = this.currentVariant();
      if (!variant) return;

      const isSub = this.mode === 'subscription';
      for (const tile of this.querySelectorAll('[data-abo-mode]')) {
        const active = tile.dataset.aboMode === this.mode;
        tile.classList.toggle('is-active', active);
        tile.setAttribute('aria-pressed', String(active));
      }

      const intervals = this.querySelector('[data-abo-intervals]');
      const summary = this.querySelector('[data-abo-summary]');
      if (intervals) intervals.hidden = !isSub;
      if (summary) summary.hidden = !isSub;

      const planId = this.selectedPlanId();
      const plan = isSub && planId ? variant.plans[planId] : null;

      if (this.planInput) {
        this.planInput.disabled = !plan;
        this.planInput.value = plan ? planId : '';
      }

      const oneTimeEl = this.querySelector('[data-abo-onetime-price]');
      if (oneTimeEl) oneTimeEl.textContent = this.money(variant.price);

      const subPriceEl = this.querySelector('[data-abo-sub-price]');
      const recurringPrices = Object.values(variant.plans).map((p) => p.recurring);
      if (subPriceEl && recurringPrices.length) {
        const fromPrefix = this.strings.fromPrefix || 'ab';
        subPriceEl.textContent = `${fromPrefix} ${this.money(Math.min(...recurringPrices))}`;
      }

      if (plan) {
        const firstEl = this.querySelector('[data-abo-price-first]');
        const firstLabelEl = this.querySelector('[data-abo-label-first]');
        const recurringEl = this.querySelector('[data-abo-price-recurring]');
        const recurringLine = this.querySelector('[data-abo-line-recurring]');
        const samePrice = plan.first === plan.recurring;

        if (firstEl) firstEl.textContent = this.money(plan.first);
        if (firstLabelEl) {
          firstLabelEl.textContent = samePrice
            ? this.strings.perDelivery || 'Pro Lieferung'
            : this.strings.firstDelivery || '1. Lieferung';
        }
        if (recurringEl) recurringEl.textContent = this.money(plan.recurring);
        if (recurringLine) recurringLine.hidden = samePrice;

        // Ersparnis-Badges und Durchstreichpreise live berechnen
        const base = variant.price;
        const pctFirst = base > 0 ? Math.round(((base - plan.first) / base) * 100) : 0;
        const pctRecurring = base > 0 ? Math.round(((base - plan.recurring) / base) * 100) : 0;
        const pairs = [
          ['[data-abo-save-first]', '[data-abo-compare-first]', pctFirst],
          ['[data-abo-save-recurring]', '[data-abo-compare-recurring]', pctRecurring],
        ];
        for (const [saveSel, compareSel, pct] of pairs) {
          const saveEl = this.querySelector(saveSel);
          const compareEl = this.querySelector(compareSel);
          if (saveEl) {
            saveEl.textContent = `−${pct}%`;
            saveEl.hidden = pct <= 0;
          }
          if (compareEl) {
            compareEl.textContent = this.money(base);
            compareEl.hidden = pct <= 0;
          }
        }
      }

      this.dispatchEvent(
        new CustomEvent('altruan-abo:change', {
          bubbles: true,
          detail: {
            mode: this.mode,
            sellingPlanId: plan ? Number(planId) : null,
          },
        })
      );
    }
  }

  customElements.define('altruan-abo-widget', AltruanAboWidget);
})();
